import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, Store, Tags } from 'lucide-react';
import { useTemuWorkspaceAuth } from '@/temu-platform/context/TemuWorkspaceAuthContext';
import TemuGateSelect from '@/temu-platform/components/TemuGateSelect';
import {
  TEMU_ACTIVE_SHOP_STORAGE_KEY,
  TEMU_SHOP_TYPES,
  buildTemuPath,
  normalizeTemuShopType,
  trackForShopType,
} from '@/temu-platform/constants/temuRoutes';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import {
  listAuthorizedTemuShops,
} from '@/temu-platform/utils/temuShopAuthGate';
import { gateCopyForTemuShopType } from '@/temu-platform/utils/temuGateCopy';
import { localizedShopTypeLabel } from '@/i18n/shopTypeText';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
} from '@/utils/platformShopScope';
import '@/styles/temu-platform/temu-gate.css';

const GATE_HERO = `${process.env.PUBLIC_URL || ''}/temu-gate/hero.png?v=3`;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readStoredShopId() {
  try {
    return localStorage.getItem(TEMU_ACTIVE_SHOP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function persistActiveShopId(shopId) {
  try {
    if (shopId) {
      localStorage.setItem(TEMU_ACTIVE_SHOP_STORAGE_KEY, String(shopId));
    } else {
      localStorage.removeItem(TEMU_ACTIVE_SHOP_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function resolveRedirect(searchParams, locationState, shopType) {
  const fromQuery = searchParams.get('redirect');
  if (fromQuery) return fromQuery;
  if (locationState?.redirect) return locationState.redirect;
  const track = trackForShopType(shopType) || 'full';
  return buildTemuPath(track, 'home');
}

/** 门禁店铺选项：仅展示名称与店铺类型，不带授权槽摘要 */
function shopOptionLabel(shop, t) {
  const name = String(shop?.shopName || '').trim() || t('temuGate.shopFallback', { id: shop?.id });
  const type = localizedShopTypeLabel(t, normalizeTemuShopType(shop?.shopType));
  return type ? t('temuGate.shopOption', { name, type }) : name;
}

function useGateStoryMotion(headlineText, highlightCount) {
  const [headline, setHeadline] = useState('');
  const [showLead, setShowLead] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [typingDone, setTypingDone] = useState(false);
  const safeCount = Math.max(1, Number(highlightCount) || 1);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setHeadline(headlineText);
      setShowLead(true);
      setVisibleCount(safeCount);
      setTypingDone(true);
      return undefined;
    }

    let cancelled = false;
    let charIndex = 0;
    let typeTimer;
    let leadTimer;
    let revealTimer;
    let cycleTimer;

    setHeadline('');
    setShowLead(false);
    setVisibleCount(0);
    setActiveIndex(0);
    setTypingDone(false);

    const typeNext = () => {
      if (cancelled) return;
      charIndex += 1;
      setHeadline(headlineText.slice(0, charIndex));
      if (charIndex < headlineText.length) {
        typeTimer = window.setTimeout(typeNext, 38);
        return;
      }
      setTypingDone(true);
      leadTimer = window.setTimeout(() => {
        if (cancelled) return;
        setShowLead(true);
        let shown = 0;
        const revealNext = () => {
          if (cancelled) return;
          shown += 1;
          setVisibleCount(shown);
          if (shown < safeCount) {
            revealTimer = window.setTimeout(revealNext, 280);
            return;
          }
          cycleTimer = window.setInterval(() => {
            if (cancelled) return;
            setActiveIndex((prev) => (prev + 1) % safeCount);
          }, 2400);
        };
        revealNext();
      }, 180);
    };

    typeTimer = window.setTimeout(typeNext, 160);

    return () => {
      cancelled = true;
      window.clearTimeout(typeTimer);
      window.clearTimeout(leadTimer);
      window.clearTimeout(revealTimer);
      window.clearInterval(cycleTimer);
    };
  }, [headlineText, safeCount]);

  return { headline, showLead, visibleCount, activeIndex, typingDone };
}

export default function TemuPlatformGatePage() {
  const { t, i18n } = useTranslation('workbench');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { unlocked, unlock, lock, workspaceShopType } = useTemuWorkspaceAuth();
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopType, setSelectedShopType] = useState(
    () => normalizeTemuShopType(searchParams.get('shopType')) || TEMU_SHOP_TYPES[0],
  );
  const [selectedShopId, setSelectedShopId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(() => (
    location.state?.shopAuthError ? String(location.state.shopAuthError) : ''
  ));
  const [submitting, setSubmitting] = useState(false);

  const preferredShopId = useMemo(
    () => String(searchParams.get('shopId') || '').trim(),
    [searchParams],
  );

  const gateCopy = useMemo(
    () => {
      void i18n.language;
      return gateCopyForTemuShopType(selectedShopType);
    },
    [selectedShopType, i18n.language],
  );
  const highlightItems = gateCopy.highlights || [];
  const { headline, showLead, visibleCount, activeIndex, typingDone } = useGateStoryMotion(
    gateCopy.headline,
    highlightItems.length,
  );

  const sessionTypeMatches = Boolean(
    unlocked
    && workspaceShopType
    && normalizeTemuShopType(workspaceShopType) === normalizeTemuShopType(selectedShopType),
  );
  const needsPassword = !sessionTypeMatches;

  const shopsOfType = useMemo(
    () => shops.filter(
      (shop) => normalizeTemuShopType(shop?.shopType) === normalizeTemuShopType(selectedShopType),
    ),
    [shops, selectedShopType],
  );

  const shopTypeOptions = useMemo(
    () => TEMU_SHOP_TYPES.map((type) => ({
      value: type,
      label: localizedShopTypeLabel(t, type),
    })),
    [t],
  );

  const shopOptions = useMemo(() => {
    const options = shopsOfType.map((shop) => ({
      value: String(shop.id),
      label: shopOptionLabel(shop, t),
    }));
    if (shopsOfType.length >= 2) {
      return [
        { value: PLATFORM_ALL_SHOPS_ID, label: t('temuGate.allAuthorized') },
        ...options,
      ];
    }
    return options;
  }, [shopsOfType, t]);

  useEffect(() => {
    if (location.state?.shopAuthError) {
      setError(String(location.state.shopAuthError));
      return;
    }
    if (searchParams.get('shopAuthExpired') === '1') {
      setError(t('shop.enterAuthExpired'));
    }
  }, [location.state, searchParams, t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setShopsLoading(true);
      try {
        const list = await listAuthorizedTemuShops();
        if (cancelled) return;
        setShops(list);
        if (!list.length) {
          setSelectedShopId('');
          setError((prev) => prev || t('shop.enterAuthExpired'));
          return;
        }
        const preferred = preferredShopId
          ? list.find((shop) => String(shop.id) === String(preferredShopId))
          : null;
        if (preferred) {
          const type = normalizeTemuShopType(preferred.shopType) || TEMU_SHOP_TYPES[0];
          setSelectedShopType(type);
          setSelectedShopId(String(preferred.id));
          return;
        }
        const stored = readStoredShopId();
        if (isAllShopsScope(stored)) {
          const preferredType = TEMU_SHOP_TYPES[0];
          const preferredShops = list.filter(
            (shop) => normalizeTemuShopType(shop?.shopType) === preferredType,
          );
          if (preferredShops.length >= 2) {
            setSelectedShopType(preferredType);
            setSelectedShopId(PLATFORM_ALL_SHOPS_ID);
            return;
          }
          for (const type of TEMU_SHOP_TYPES) {
            const ofType = list.filter(
              (shop) => normalizeTemuShopType(shop?.shopType) === type,
            );
            if (ofType.length >= 2) {
              setSelectedShopType(type);
              setSelectedShopId(PLATFORM_ALL_SHOPS_ID);
              return;
            }
          }
        }
        const storedShop = list.find((shop) => String(shop.id) === String(stored));
        if (storedShop) {
          setSelectedShopType(normalizeTemuShopType(storedShop.shopType) || TEMU_SHOP_TYPES[0]);
          setSelectedShopId(String(storedShop.id));
        }
      } catch {
        if (!cancelled) {
          setShops([]);
          setError(t('shop.enterAuthExpired'));
        }
      } finally {
        if (!cancelled) setShopsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [preferredShopId, t]);

  useEffect(() => {
    if (!selectedShopId) return;
    if (isAllShopsScope(selectedShopId)) {
      if (shopsOfType.length < 2) setSelectedShopId('');
      return;
    }
    const stillVisible = shopsOfType.some((shop) => String(shop.id) === String(selectedShopId));
    if (!stillVisible) setSelectedShopId('');
  }, [shopsOfType, selectedShopId]);

  const applySelectedShop = async (shopId) => {
    const id = String(shopId || '').trim();
    if (!id) return;
    persistActiveShopId(id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!selectedShopType) {
      setError(t('shop.needShopType'));
      return;
    }
    if (!selectedShopId) {
      setError(t('temuGate.needShop'));
      return;
    }
    const enteringAll = isAllShopsScope(selectedShopId);
    if (enteringAll) {
      if (shopsOfType.length < 2) {
        setError(t('temuGate.needTwoShops'));
        return;
      }
    } else {
      const selected = shops.find((shop) => String(shop.id) === String(selectedShopId));
      if (!selected
        || normalizeTemuShopType(selected.shopType) !== normalizeTemuShopType(selectedShopType)) {
        setError(t('temuGate.shopUnavailable'));
        return;
      }
    }
    if (needsPassword && !password.trim()) {
      setError(unlocked ? t('temuGate.needPasswordSwitch') : t('temuGate.needPassword'));
      return;
    }
    setSubmitting(true);
    try {
      if (needsPassword) {
        await unlock(password, selectedShopType);
      }
      const list = await listAuthorizedTemuShops({ shopType: selectedShopType });
      if (enteringAll) {
        if (list.length < 2) {
          await lock();
          setShops(list);
          setSelectedShopId('');
          setError(t('shop.enterAuthExpired'));
          return;
        }
      } else {
        const stillOk = list.some((shop) => String(shop.id) === String(selectedShopId));
        if (!stillOk) {
          await lock();
          setShops(list);
          setSelectedShopId('');
          setError(t('shop.enterAuthExpired'));
          return;
        }
      }
      await applySelectedShop(selectedShopId);
      navigate(resolveRedirect(searchParams, location.state, selectedShopType), { replace: true });
    } catch (err) {
      const msg = err?.code === 'TEMU_PASSWORD_INVALID'
        ? t('temuGate.wrongPassword')
        : (err?.message || t('temuGate.passwordFailed'));
      setError(msg);
      if (needsPassword) setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    window.location.href = `${IPE_WORKBENCH_PATH}?activeNav=sheinHub`;
  };

  return (
    <div className="temu-gate">
      <div
        className="temu-gate__hero"
        style={{ backgroundImage: `url(${GATE_HERO})` }}
        aria-hidden="true"
      />
      <div className="temu-gate__veil" aria-hidden="true" />
      <div className="temu-gate__stage">
        <section className="temu-gate__story" aria-live="polite">
          <p className="temu-gate__kicker">IPE × TEMU · {localizedShopTypeLabel(t, selectedShopType)}</p>
          <h1 className="temu-gate__brand">IPE</h1>
          <p className={`temu-gate__headline${typingDone ? ' is-done' : ' is-typing'}`}>
            <span>{headline}</span>
            {!typingDone ? <i className="temu-gate__caret" aria-hidden="true" /> : null}
          </p>
          <p className={`temu-gate__lead${showLead ? ' is-visible' : ''}`}>
            {gateCopy.lead}
          </p>
          <ul className="temu-gate__highlights">
            {highlightItems.map((item, index) => {
              const visible = index < visibleCount;
              const active = visible && index === activeIndex && visibleCount >= highlightItems.length;
              return (
                <li
                  key={`${selectedShopType}-${item.title}`}
                  className={`temu-gate__highlight${visible ? ' is-visible' : ''}${active ? ' is-active' : ''}`}
                >
                  <span className="temu-gate__highlight-mark" aria-hidden="true" />
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.desc}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <aside className="temu-gate__auth" aria-label={t('temuGate.authAria')}>
          <div className="temu-gate__auth-glow" aria-hidden="true" />
          <div className="temu-gate__panel">
            <div className="temu-gate__header">
              <span className="temu-gate__brand-badge">TEMU</span>
              <h2>{t('temuGate.title')}</h2>
            </div>
            <p className="temu-gate__panel-desc">
              {t('temuGate.panelLead')}
            </p>

            <form className="temu-gate__form" onSubmit={handleSubmit} autoComplete="off">
              {/* 诱饵字段：承接浏览器账号密码自动填充，避免写入店铺搜索框 */}
              <div className="temu-gate__autofill-trap" aria-hidden="true">
                <input type="text" name="username" autoComplete="username" tabIndex={-1} defaultValue="" />
                <input type="password" name="password" autoComplete="current-password" tabIndex={-1} defaultValue="" />
              </div>
              <label className="temu-gate__field">
                <span>{t('temuGate.hostingType')}</span>
                <span className="temu-gate__input-wrap temu-gate__input-wrap--select">
                  <Tags size={16} aria-hidden="true" />
                  <TemuGateSelect
                    value={selectedShopType}
                    options={shopTypeOptions}
                    disabled={shopsLoading || submitting}
                    placeholder={t('temuGate.hostingTypePlaceholder')}
                    onChange={(next) => {
                      setSelectedShopType(next);
                      setSelectedShopId('');
                      setError('');
                    }}
                  />
                </span>
              </label>

              <div className="temu-gate__field">
                <span>{t('temuGate.loginShop')}</span>
                <span
                  className="temu-gate__input-wrap temu-gate__input-wrap--select"
                  onMouseDown={(e) => {
                    if (e.target?.closest?.('.temu-gate-select__input')) return;
                    if (e.target?.closest?.('.temu-gate-select__clear')) return;
                    const input = e.currentTarget.querySelector('input.temu-gate-select__input');
                    if (!input || input.disabled) return;
                    e.preventDefault();
                    input.focus();
                  }}
                >
                  <Store size={16} aria-hidden="true" />
                  <TemuGateSelect
                    value={selectedShopId}
                    options={shopOptions}
                    disabled={shopsLoading || submitting || shops.length === 0}
                    placeholder={shopsLoading ? t('temuGate.loadingShops') : t('temuGate.shopSearchPlaceholder')}
                    emptyText={t('temuGate.noShops')}
                    searchable
                    allowClear
                    inputName="temu-gate-shop"
                    onChange={(next) => setSelectedShopId(next)}
                  />
                </span>
              </div>

              {needsPassword ? (
                <label className="temu-gate__field">
                  <span>{t('temuGate.passwordLabel')}</span>
                  <span className="temu-gate__input-wrap temu-gate__input-wrap--password">
                    <Lock size={16} aria-hidden="true" />
                    <input
                      id="temu-gate-password"
                      name="temu-gate-ipe-verify"
                      className="temu-gate__password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('temuGate.passwordPlaceholder')}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                      required
                    />
                    <button
                      type="button"
                      className="temu-gate__password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? t('temuGate.hidePassword') : t('temuGate.showPassword')}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </span>
                </label>
              ) : (
                <p className="temu-gate__session-hint">{t('temuGate.sessionHint')}</p>
              )}

              {error ? <p className="temu-gate__error">{error}</p> : null}

              <div className="temu-gate__actions">
                <button
                  type="button"
                  className="temu-gate__btn temu-gate__btn--ghost"
                  onClick={handleCancel}
                >
                  {t('temuGate.backWorkbench')}
                </button>
                <button
                  type="submit"
                  className="temu-gate__btn temu-gate__btn--primary"
                  disabled={submitting || shopsLoading || shops.length === 0}
                >
                  {submitting ? t('temuGate.entering') : t('temuGate.confirmEnter')}
                </button>
              </div>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}
